import { TConvertor, TCreateConverter, TFetchConverter } from 'data/types/converter.types';
import { dateTimeFormat, formattedDateTimezone } from 'helpers/utils';
import {
  TPropertyWithAccessibilityInfoDB,
  TPropertyWithAccessibilityInfoUI,
  TPropertyConfigNoteDB,
  TPropertyConfigNoteUI,
  TPropertyConfigSetCreateDB,
  TPropertyConfigSetCreateUI,
  TPropertyConfigSetGetDB,
  TPropertyConfigSetGetUI,
  TPropertyConfigSetEditUI,
  TPropertyConfigSetEditDB,
  TPropertyConfigEditNoteUI,
  TPropertyConfigEditNoteDB,
  TPropertyConfigSetPetRestrictionsDB,
  TPropertyConfigSetPetRestrictionsUI,
  TSpeciesRequirementUI,
  ESpeciesRequirementDB,
  TRequiredDocumentsUI,
  TRequiredDocumentsDB,
  TCustomRequiredDocumentUI,
  TCustomRequiredDocumentDB,
  ERequiredDocument,
  TCustomRequiredDocumentsUI,
  EConfigSetChangeActionTypeDB,
  TConfigSetChangeDB,
  TConfigSetChangeLogGroupDB,
  TConfigSetChangeLogGroupUI,
} from '../types/propertyConfigSet.types';
import { EAnimalTypes } from '../types/animals.types';
import { animalTypeConverter } from './animals.converter';
import { TPropertiesConfigEditTypeDB, TPropertiesConfigEditTypeUI } from '../types/property.types';

export const propertyConfigNoteConverter: TConvertor<TPropertyConfigNoteUI, TPropertyConfigNoteDB> = {
  fromDb: (data) => {
    return {
      id: data.id,
      title: data.title || '',
      description: data.description,
    };
  },
  toDb: (data) => ({
    ...(data.id && { id: data.id }),
    title: data.title,
    description: data.description,
  }),
};

export const propertyConfigNoteEditConverter: TCreateConverter<TPropertyConfigEditNoteUI, TPropertyConfigEditNoteDB> = {
  toDb: (data) => ({
    animalVerificationConfigurationId: data.animalRequirementsSetId,
    note: propertyConfigNoteConverter.toDb(data.note),
    newConfigurationSetName: data.name || undefined,
    propertyId: data.name ? data.propertyId : undefined,
  }),
};

export const configSetAssignedPropertyConverter: TFetchConverter<
  TPropertyWithAccessibilityInfoUI,
  TPropertyWithAccessibilityInfoDB
> = {
  fromDb: (data) => {
    return {
      id: data.id,
      name: data.name,
      beneficiaryUserHasAccessToProperty: data.beneficiaryUserHasAccessToProperty,
    };
  },
};

export const propertyConfigSetPetRestrictionsGetConverter: TFetchConverter<
  TPropertyConfigSetPetRestrictionsUI,
  TPropertyConfigSetPetRestrictionsDB
> = {
  fromDb: (data) => {
    return {
      breeds: data.restrictedBreeds,
      species: data.allowedSpecies,

      maxWeightLimit: data.maxPetWeight,
      maxNumberOfPets: data.maxNumOfPetsAllowed,
      isPetAllowed: !data.noPetPolicy ? 'yes' : 'no',
    };
  },
};

export const configSetSpiciesRequirementGetConverter: TFetchConverter<TSpeciesRequirementUI, ESpeciesRequirementDB[]> =
  {
    fromDb: (data) => {
      return {
        dog: data.includes(ESpeciesRequirementDB.DOG),
        cat: data.includes(ESpeciesRequirementDB.CAT),
      };
    },
  };

export const configSetRequiredDocumentsGetConverter: TFetchConverter<
  {
    custom: TCustomRequiredDocumentUI;
    predefined: TRequiredDocumentsUI[];
  },
  TRequiredDocumentsDB[]
> = {
  fromDb: (data) => {
    const custom: TCustomRequiredDocumentUI[] = [];
    const predefined: TRequiredDocumentsUI = { ...mockRequiredDocumentsDB };

    data.forEach((item) => {
      if (item.predefined) {
        (predefined as Record<string, boolean | TSpeciesRequirementUI>)[item.name.toUpperCase()] =
          configSetSpiciesRequirementGetConverter.fromDb(item.applicableSpecies);
      } else {
        custom.push({
          hasExpirationDate: item.expirable ? 'yes' : 'no',
          id: item.name,
          name: item.name,
          requiredSpecies: configSetSpiciesRequirementGetConverter.fromDb(item.applicableSpecies),
          requiredAnimalTypes: item.applicableAnimalTypes || [],
        });
      }
    });
    return { custom, predefined };
  },
};

export const configSetCustomRequiredDocumentsGetConverter: TFetchConverter<
  TCustomRequiredDocumentUI,
  TCustomRequiredDocumentDB
> = {
  fromDb: (data) => {
    return {
      id: data.id,
      name: data.name,
      hasExpirationDate: data.hasExpirationDate ? 'yes' : 'no',
      requiredSpecies: configSetSpiciesRequirementGetConverter.fromDb(data.requiredSpecies || []),
      requiredAnimalTypes: data.requiredAnimalTypes?.map(animalTypeConverter.fromDb) ?? [],
    };
  },
};

export const mockRequiredDocumentsDB: TRequiredDocumentsUI = {
  [ERequiredDocument.RABIES_VACCINATION]: false,
  [ERequiredDocument.CITY_LICENSE]: false,
  [ERequiredDocument.SPAYED_NEUTERED_DOCUMENT]: false,
  [ERequiredDocument.DOG_BITE_LIABILITY_INSURANCE]: false,
  [ERequiredDocument.PARVO]: false,
};
// TODO: remove when BE is ready
export const mockCustomRequiredDocumentsDB: TCustomRequiredDocumentDB[] = [
  {
    id: 'doc-1',
    name: 'Proof of Training',
    hasExpirationDate: true,
    requiredSpecies: [ESpeciesRequirementDB.DOG],
    requiredAnimalTypes: [EAnimalTypes.PET],
  },
  {
    id: 'doc-2',
    name: 'Breed Certification',
    hasExpirationDate: false,
    requiredSpecies: [ESpeciesRequirementDB.CAT],
    requiredAnimalTypes: [EAnimalTypes.ESA, EAnimalTypes.SA],
  },
];
export const propertyConfigSetGetConverter: TFetchConverter<TPropertyConfigSetGetUI, TPropertyConfigSetGetDB> = {
  fromDb: (data) => {
    const { custom, predefined } = configSetRequiredDocumentsGetConverter.fromDb(data.requiredVetRecords || []);
    return {
      id: data.id,
      name: data.name,
      requirements: data.requirements,
      // Absent on every set saved before the tri-state existed, which reads as "nothing hidden" —
      // so those sets keep behaving exactly as they did.
      hiddenRequirements: data.hiddenRequirements || [],
      notes: data.notes.map((note) => propertyConfigNoteConverter.fromDb(note)),
      isDefault: data.isDefault,
      properties: (data?.properties || []).map((property) => configSetAssignedPropertyConverter.fromDb(property)),
      tenantShouldPayForPet: data.tenantShouldPayForPet,
      petRestrictions: data.petRestrictions
        ? propertyConfigSetPetRestrictionsGetConverter.fromDb(data.petRestrictions)
        : { isPetAllowed: 'no' },
      requiredDocuments: predefined,
      customRequiredDocuments: custom,
      reVerifyKnownAssistanceAnimals: !!data?.reVerifyKnownAssistanceAnimals,
      isCourseRequired: !!data?.isAnimalCurriculumRequired,
      hideDocumentsSection: !!data?.hideDocumentsSection,
    };
  },
};

export const propertyConfigSetCreateConverter: TCreateConverter<
  TPropertyConfigSetCreateUI,
  TPropertyConfigSetCreateDB
> = {
  toDb: (data) => {
    return {
      animalVerificationCriteria: data.animalRequirements,
      hiddenAnimalVerificationCriteria: data.hiddenAnimalRequirements || [],
      name: data.name,
      notes:
        data.notes.length &&
        data.notes
          ?.filter((item) => item.title !== undefined && item.description !== undefined)
          .map((note) => propertyConfigNoteConverter.toDb(note)),
      propertyIds: data.propertyIds,
      petRestrictions:
        data.isPetAllowed === 'yes'
          ? {
              maxPetWeight: data.maxPetWeight || null,
              maxNumOfPetsAllowed: data.maxPets || null,
              allowedSpecies: data.species,
              restrictedBreeds: data.restrictedBreeds,
            }
          : null,
      noPetPolicy: data.isPetAllowed === 'no',
      requiredVetRecords: [
        ...requiredVetRecordsConverter.toDb(data.requiredDocuments),
        ...(data.customRequiredDocuments || []).map((item) => customRequiredDocumentConverter.toDb(item)),
      ],
      reVerifyKnownAssistanceAnimals: !!data?.reVerifyKnownAssistanceAnimals,
      isAnimalCurriculumRequired: !!data?.isCourseRequired,
      hideDocumentsSection: !!data?.hideDocumentsSection,
    };
  },
};

export const requiredVetRecordsConverter: TCreateConverter<TRequiredDocumentsUI, TRequiredDocumentsDB[]> = {
  toDb: (data) => {
    return Object.entries(data)
      .filter(([, value]) => {
        if (typeof value === 'object' && value !== null) {
          return Object.values(value).some((v) => v);
        }
        return value === true;
      })
      .map(([name, value]) => {
        let applicableSpecies: string[] = [];

        if (typeof value === 'object' && value !== null) {
          applicableSpecies = Object.entries(value)
            .filter(([, isApplicable]) => isApplicable)
            .map(([species]) => species.toUpperCase());
        } else if (value === true) {
          const match = name.match(/^(DOG)/i);
          if (match) {
            applicableSpecies = [match[1].toUpperCase()];
          }
        }

        return {
          expirable: true,
          name,
          applicableSpecies,
          predefined: true,
        };
      });
  },
};

export const customRequiredDocumentConverter: TCreateConverter<TCustomRequiredDocumentsUI, TRequiredDocumentsDB> = {
  toDb: (data) => {
    const species: string[] = [];

    if (data.requiredSpecies.dog) {
      species.push('DOG');
    }
    if (data.requiredSpecies.cat) {
      species.push('CAT');
    }

    return {
      name: data.name,
      applicableSpecies: species,
      expirable: data.hasExpirationDate === 'yes',
      predefined: false,
      applicableAnimalTypes: data.requiredAnimalTypes || [],
    };
  },
};

export const propertyConfigSetEditConverter: TCreateConverter<TPropertyConfigSetEditUI, TPropertyConfigSetEditDB> = {
  toDb: (data) => ({
    animalVerificationConfigurationId: data.id,
    criteria: data.requirements,
    hiddenCriteria: data.hiddenRequirements || [],
    name: data.name,
    newlyAssignedProperties: data.newAssignedPropertyIds,
    newlyUnassignedProperties: data.unassignedPropertyIds,
    replacingAnimalVerificationConfigurationId: data.replacingAnimalVerificationConfigurationId,
    noPetPolicy: data.isPetAllowed === 'no',
    petRestrictions:
      data.isPetAllowed === 'yes'
        ? {
            allowedSpecies: data.species,
            maxNumOfPetsAllowed: data.maxPets,
            restrictedBreeds: data.restrictedBreeds,
            ...(data.maxPetWeight && { maxPetWeight: data.maxPetWeight }),
          }
        : undefined,
    requiredVetRecords: [
      ...requiredVetRecordsConverter.toDb(data.requiredDocuments),
      ...(data.customRequiredDocuments || []).map((item) => customRequiredDocumentConverter.toDb(item)),
    ],
    reVerifyKnownAssistanceAnimals: !!data?.reVerifyKnownAssistanceAnimals,
    isAnimalCurriculumRequired: !!data?.isCourseRequired,
    hideDocumentsSection: !!data?.hideDocumentsSection,
  }),
};

export const propertyConfigSetEditerConverter: TCreateConverter<
  TPropertiesConfigEditTypeUI,
  TPropertiesConfigEditTypeDB
> = {
  toDb: (data) => {
    return {
      propertyId: data.propertyId,
      name: data.name,
      newConfigurationSetName: data.newConfigurationSetName,
      animalVerificationConfigurationId: data.animalVerificationConfigurationId,
      criteria: data.criteria,
      hiddenCriteria: data.hiddenCriteria || [],
      noPetPolicy: data.isPetAllowed === 'no',
      petRestrictions:
        data.isPetAllowed === 'yes'
          ? {
              allowedSpecies: data.species,
              maxNumOfPetsAllowed: data.maxPets,
              ...(data.maxPetWeight && { maxPetWeight: data.maxPetWeight }),
              restrictedBreeds: data.restrictedBreeds,
            }
          : undefined,
      requiredVetRecords: [
        ...requiredVetRecordsConverter.toDb(data.requiredDocuments),
        ...(data.customRequiredDocuments || []).map((item) => customRequiredDocumentConverter.toDb(item)),
      ],
      reVerifyKnownAssistanceAnimals: !!data?.reVerifyKnownAssistanceAnimals,
      isAnimalCurriculumRequired: !!data?.isCourseRequired,
      hideDocumentsSection: !!data?.hideDocumentsSection,
    };
  },
};

// Document actions splice the document name into a single label; every other action is a fixed label.
const configSetChangeActionLabels: Record<EConfigSetChangeActionTypeDB, string> = {
  [EConfigSetChangeActionTypeDB.EDITED_ASSIGNED_PROPERTIES]: 'Edited assigned properties',
  [EConfigSetChangeActionTypeDB.EDITED_RESTRICTED_BREEDS]: 'Edited selection of restricted breeds',
  [EConfigSetChangeActionTypeDB.EDITED_ALLOWED_SPECIES]: 'Edited selection of allowed species',
  [EConfigSetChangeActionTypeDB.UPDATED_MAX_PET_WEIGHT]: 'Updated pet weight maximum',
  [EConfigSetChangeActionTypeDB.UPDATED_MAX_PETS_PER_UNIT]: 'Updated max # of pets allowed per unit',
  [EConfigSetChangeActionTypeDB.ADDED_NOTE]: 'Added note',
  [EConfigSetChangeActionTypeDB.EDITED_NOTE]: 'Edited note',
  [EConfigSetChangeActionTypeDB.DELETED_NOTE]: 'Deleted note',
  [EConfigSetChangeActionTypeDB.EDITED_BASIC_ANIMAL_INFORMATION]: 'Edited basic animal information',
  [EConfigSetChangeActionTypeDB.ADDED_DOCUMENT_REQUIREMENT]: 'Added',
  [EConfigSetChangeActionTypeDB.UPDATED_DOCUMENT_REQUIREMENT]: 'Updated',
  [EConfigSetChangeActionTypeDB.REMOVED_DOCUMENT_REQUIREMENT]: 'Removed',
  [EConfigSetChangeActionTypeDB.EDITED_DOCUMENTS_SECTION_VISIBILITY]: 'Edited documents section visibility',
  [EConfigSetChangeActionTypeDB.EDITED_ANIMAL_CURRICULUM_REQUIREMENT]: 'Edited animal curriculum requirement',
};

const configSetChangeDocumentActions = new Set<EConfigSetChangeActionTypeDB>([
  EConfigSetChangeActionTypeDB.ADDED_DOCUMENT_REQUIREMENT,
  EConfigSetChangeActionTypeDB.UPDATED_DOCUMENT_REQUIREMENT,
  EConfigSetChangeActionTypeDB.REMOVED_DOCUMENT_REQUIREMENT,
]);

const configSetChangeLabelConverter: TFetchConverter<string, TConfigSetChangeDB> = {
  fromDb: (data) => {
    const label = configSetChangeActionLabels[data.actionType] || data.actionType;
    if (configSetChangeDocumentActions.has(data.actionType) && data.documentName) {
      return `${label} ${data.documentName} document requirement`;
    }
    return label;
  },
};

export const configSetChangeLogConverter: TFetchConverter<TConfigSetChangeLogGroupUI[], TConfigSetChangeLogGroupDB[]> =
  {
    fromDb: (data) =>
      (data || []).map((group) => ({
        saveGroupId: group.saveGroupId,
        occurredOn: formattedDateTimezone(group.occurredOn, dateTimeFormat),
        userName: group.userName || 'N/A',
        userRole: group.userRole || '',
        changes: (group.changes || []).map((change) => configSetChangeLabelConverter.fromDb(change)),
      })),
  };
